// src/components/AuthStatus.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../css/AuthStatus.css';

const AuthStatus = () => {
  const [status, setStatus] = useState('checking');
  const [timeLeft, setTimeLeft] = useState(null);
  const [visible, setVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const navigate = useNavigate();
  
  // Check if we're on mobile and set auto-hide timer for all devices
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      
      // When resizing from desktop to mobile, ensure visibility is reset
      if (mobile && !isMobile) {
        setVisible(true);
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    // Auto-hide after 5 seconds for both mobile AND desktop
    // But only when status is not warning or expired
    let hideTimer;
    if (visible && status !== 'warning' && status !== 'expired') {
      hideTimer = setTimeout(() => {
        setVisible(false);
      }, 5000); // 5 seconds
    }
    
    return () => {
      window.removeEventListener('resize', handleResize);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, [isMobile, visible, status]);
  
  // Check auth status periodically
  useEffect(() => {
    const checkAuthStatus = () => {
      const token = localStorage.getItem('token');
      const loginTimestamp = localStorage.getItem('loginTimestamp');
      
      if (!token || !loginTimestamp) {
        setStatus('logged-out');
        return;
      }
      
      // Calculate remaining time in minutes
      const expiryTime = parseInt(loginTimestamp) + (60 * 60 * 1000); // 1 hour expiry
      const currentTime = Date.now();
      const remainingTime = Math.max(0, expiryTime - currentTime);
      const remainingMinutes = Math.floor(remainingTime / (60 * 1000));
      
      setTimeLeft(remainingMinutes);
      
      if (remainingMinutes <= 0) {
        setStatus('expired');
        // Make banner visible when session has expired
        setVisible(true);
      } else if (remainingMinutes <= 5) {
        setStatus('warning');
        // Make banner visible when warning status is detected (for both mobile and desktop)
        setVisible(true);
      } else {
        setStatus('logged-in');
      }
    };
    
    // Check immediately
    checkAuthStatus();
    
    // Then check every 30 seconds
    const interval = setInterval(checkAuthStatus, 30000);
    
    return () => clearInterval(interval);
  }, [isMobile]);
  
  const handleRefresh = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        navigate('/login');
        return;
      }
      
      // Call your refresh token endpoint
      const response = await fetch(`${process.env.REACT_APP_API_URL}/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      });
      
      const data = await response.json();
      
      if (data.accessToken) {
        localStorage.setItem('token', data.accessToken);
        localStorage.setItem('loginTimestamp', Date.now().toString());
        setStatus('logged-in');
        setTimeLeft(60); // Reset to 60 minutes
        
        // Make visible again after successful refresh
        setVisible(true);
        
        // Set auto-hide timer for both mobile and desktop
        setTimeout(() => {
          // Only hide if status is still logged-in (not warning or expired)
          if (status === 'logged-in') {
            setVisible(false);
          }
        }, 5000);
      } else {
        navigate('/login');
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      navigate('/login');
    }
  };
  
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('loginTimestamp');
    navigate('/login');
  };
  
  // Toggle visibility manually
  const toggleVisibility = () => {
    setVisible(!visible);
  };
  
  if (status === 'checking') {
    return null; // Don't show anything while checking
  }
  
  // Add visibility classes based on state
  const statusClass = `auth-status ${status} ${isMobile ? 'mobile' : ''} ${visible ? 'visible' : 'hidden'}`;
  
  return (
    <div className={statusClass}>
      {/* Toggle button for both mobile and desktop */}
      <button className="status-toggle" onClick={toggleVisibility}>
        {visible ? '✕' : '✓'}
      </button>
      
      <div className="status-content">
        {status === 'logged-in' && (
          <>
            <span className="status-icon">✓</span>
            <span className="status-text">Logged in</span>
            {timeLeft && <span className="time-left">({timeLeft} mins)</span>}
          </>
        )}
        
        {status === 'warning' && (
          <>
            <span className="status-icon">⚠️</span>
            <span className="status-text">Session expiring soon!</span>
            {timeLeft && <span className="time-left">({timeLeft} mins)</span>}
            <button className="refresh-button" onClick={handleRefresh}>Extend Session</button>
          </>
        )}
        
        {status === 'expired' && (
          <>
            <span className="status-icon">⚠️</span>
            <span className="status-text">Session expired</span>
            <button className="login-button" onClick={() => navigate('/login')}>Log in Again</button>
          </>
        )}
        
        {status === 'logged-out' && (
          <>
            <span className="status-icon">🔒</span>
            <span className="status-text">Not logged in</span>
            <button className="login-button" onClick={() => navigate('/login')}>Log in</button>
          </>
        )}
        
        {(status === 'logged-in' || status === 'warning') && (
          <button className="logout-button" onClick={handleLogout}>Logout</button>
        )}
      </div>
    </div>
  );
};

export default AuthStatus;